import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import { Alert, Button, Select } from '@opendate-app/setlist';

const LINK_TYPES = [
  { value: 'email',  label: 'Email' },
  { value: 'social', label: 'Social' },
  { value: 'qr',     label: 'QR code' },
  { value: 'paid',   label: 'Paid ads' },
  { value: 'other',  label: 'Other' }
];

const DESTINATION_OPTIONS = [
  { value: 'website',  label: 'Your website' },
  { value: 'opendate', label: 'Opendate event page' }
];

const popupContentStyle = {
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '480px',
  maxWidth: '92vw',
  padding: '0px'
};

const blankForm = (websiteUrl) => ({
  name: '', link_type: 'email', ticket_type_id: '', destination: websiteUrl ? 'website' : 'opendate',
  utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: ''
});

const formFromLink = (link) => ({
  name:           link.name || '',
  link_type:      link.link_type || 'other',
  destination:    link.destination || 'opendate',
  ticket_type_id: link.ticket_type_id ? String(link.ticket_type_id) : '',
  utm_source:     link.utm_source || '',
  utm_medium:     link.utm_medium || '',
  utm_campaign:   link.utm_campaign || '',
  utm_content:    link.utm_content || '',
  utm_term:       link.utm_term || ''
});

const TrackableLinkModal = ({ confirmId, teamId, csrfToken, link, ticketTypeOptions = [], websiteUrl = null, onClose, onSaved }) => {
  const isEdit = link != null;
  const [form, setForm] = useState(() => (isEdit ? formFromLink(link) : blankForm(websiteUrl)));
  const toWebsite = form.destination === 'website';
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);

    const baseUrl = `/teams/${teamId}/confirms/${confirmId}/trackable_links`;
    const url = isEdit ? `${baseUrl}/${link.id}` : baseUrl;
    const method = isEdit ? 'PATCH' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          trackable_link: {
            ...form,
            ticket_type_id: !toWebsite && form.ticket_type_id ? Number(form.ticket_type_id) : null
          }
        })
      });
      if (!response.ok) {
        const body = await response.json();
        throw new Error((body.errors || ['Unable to save link']).join(', '));
      }
      const savedLink = await response.json();
      onSaved(savedLink);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEdit ? 'Edit trackable link' : 'New trackable link';
  const submitLabel = isEdit
    ? (submitting ? 'Saving…' : 'Save changes')
    : (submitting ? 'Creating…' : 'Create link');

  return (
    <Popup
      open
      modal
      closeOnDocumentClick={!submitting}
      closeOnEscape={!submitting}
      onClose={onClose}
      contentStyle={popupContentStyle}
    >
      <div className="mi-modal">
        <header className="mi-modal__header">
          <h3 className="mi-modal__title">{title}</h3>
        </header>
        <form onSubmit={submit}>
          <div className="mi-modal__fields">
            <label>
              <span className="mi-modal__field-label">Name</span>
              <input type="text" required value={form.name} onChange={update('name')} />
            </label>
            <label>
              <span className="mi-modal__field-label">Type</span>
              <select value={form.link_type} onChange={update('link_type')}>
                {LINK_TYPES.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </label>
            {!isEdit && websiteUrl && (
              <Select
                label="Send people to"
                options={DESTINATION_OPTIONS}
                value={form.destination}
                onChange={(value) => setForm((prev) => ({ ...prev, destination: value || 'opendate' }))}
              />
            )}
            {!isEdit && toWebsite && (
              <Alert
                size="full-width"
                title="Make sure your checkout is embedded"
                description={(
                  <>
                    Visits and sales only count toward this link if{' '}
                    <strong className="mi-modal__url">{websiteUrl}</strong>{' '}
                    has the Opendate checkout embedded.
                  </>
                )}
              />
            )}
            {isEdit && toWebsite && (
              <p className="mi-modal__hint">
                Sends people to your website:{' '}
                <strong className="mi-modal__url">{link.website_url}</strong>
              </p>
            )}
            {!toWebsite && (
              <label>
                <span className="mi-modal__field-label">Restrict to ticket type (optional)</span>
                <select value={form.ticket_type_id} onChange={update('ticket_type_id')}>
                  <option value="">No restriction — show all</option>
                  {ticketTypeOptions.map((tt) => <option key={tt.id} value={String(tt.id)}>{tt.name}</option>)}
                </select>
              </label>
            )}
            {isEdit && (
              <p className="mi-modal__hint">
                UTM values are locked after a link is created — they’re baked into the short link.
                To use different UTMs, create a new link.
              </p>
            )}
            <label>
              <span className="mi-modal__field-label">UTM source</span>
              <input type="text" value={form.utm_source} onChange={update('utm_source')} readOnly={isEdit} />
            </label>
            <label>
              <span className="mi-modal__field-label">UTM medium</span>
              <input type="text" value={form.utm_medium} onChange={update('utm_medium')} readOnly={isEdit} />
            </label>
            <label>
              <span className="mi-modal__field-label">UTM campaign</span>
              <input type="text" value={form.utm_campaign} onChange={update('utm_campaign')} readOnly={isEdit} />
            </label>
            <details className="mi-modal__advanced">
              <summary>Advanced</summary>
              <div className="mi-modal__fields">
                <label>
                  <span className="mi-modal__field-label">UTM content</span>
                  <input type="text" value={form.utm_content} onChange={update('utm_content')} readOnly={isEdit} />
                </label>
                <label>
                  <span className="mi-modal__field-label">UTM term</span>
                  <input type="text" value={form.utm_term} onChange={update('utm_term')} readOnly={isEdit} />
                </label>
              </div>
            </details>
          </div>
          {error && <p className="mi-modal__error">{error}</p>}
          <footer className="mi-modal__footer">
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </footer>
        </form>
      </div>
    </Popup>
  );
};

TrackableLinkModal.propTypes = {
  confirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  csrfToken: PropTypes.string.isRequired,
  link: PropTypes.object,
  ticketTypeOptions: PropTypes.array,
  websiteUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

export default TrackableLinkModal;
